import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import WidgetFooter from '../../components/WidgetFooter';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { useClientSavedAddresses } from '../../hooks/useClientSavedAddresses';
import { toast } from 'sonner';
import { 
  Leaf, ChevronRight, ChevronLeft, Check, User, Briefcase, 
  Calendar, Calculator, Send, Loader2, Info, MapPin, Mail,
  Phone, Clock, Euro, ExternalLink, CalendarDays, Plus, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { widgetApi } from '../../lib/api';
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isAfter, isBefore, startOfWeek, endOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';

const STEPS = [
  { id: 'services', title: 'Prestations', icon: Briefcase },
  { id: 'planning', title: 'Planning', icon: Calendar },
  { id: 'contact', title: 'Coordonnées', icon: User },
  { id: 'summary', title: 'Récapitulatif', icon: Calculator },
];

// Duration options
const DURATION_OPTIONS = [
  { value: 'half_day', label: '½ journée', hours: 3.5 },
  { value: 'full_day', label: 'Journée complète', hours: 7 },
];

// Default gardening/landscaping services for personal services
const DEFAULT_SERVICES = [
  { id: 'tonte', label: 'Tonte de pelouse', icon: '🌿' },
  { id: 'taille_haies', label: 'Taille de haies', icon: '✂️' },
  { id: 'desherbage', label: 'Désherbage', icon: '🌱' },
  { id: 'ramassage_feuilles', label: 'Ramassage de feuilles', icon: '🍂' },
  { id: 'taille_arbustes', label: 'Taille d\'arbustes', icon: '🌳' },
  { id: 'entretien_massifs', label: 'Entretien des massifs', icon: '🌺' },
  { id: 'debroussaillage', label: 'Débroussaillage', icon: '🪓' },
  { id: 'arrosage', label: 'Arrosage', icon: '💧' },
  { id: 'scarification', label: 'Scarification', icon: '🔧' },
  { id: 'evacuation_dechets', label: 'Évacuation des déchets verts', icon: '🚛' },
  { id: 'autre', label: 'Autre (préciser)', icon: '📝' },
];

export default function BookingWidget() {
  const { companyId } = useParams();
  const [searchParams] = useSearchParams();
  const { addresses: savedAddresses } = useClientSavedAddresses();
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [config, setConfig] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [availability, setAvailability] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Custom theme from URL params
  const primaryColor = searchParams.get('primary') || '#8BC34A';
  const secondaryColor = searchParams.get('secondary') || '#4CAF50';
  const textColor = searchParams.get('text') || '#1a1a1a';

  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Services/Prestations
    selected_services: [], // Array of service IDs
    other_service_description: '', // If "autre" is selected
    needs_description: '',
    slot_duration: 'half_day', // half_day (3.5h) or full_day (7h)
    
    // Step 2: Selected slots
    selected_slots: [], // Array of { date: Date, period: 'morning' | 'afternoon' | 'full' }
    
    // Step 3: Contact
    client_name: '',
    client_email: '',
    client_phone: '',
    client_address: '',
    client_coords: null,
    urssaf_email: '', // Email lié aux services des impôts
    
    // Additional notes
    notes: '',
  });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await widgetApi.getConfig(companyId);
        setConfig(response.data);
        setAvailability(response.data.availability || []);
      } catch (error) {
        console.error('Error loading widget config:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchConfig();
  }, [companyId]);

  const hourlyRate = config?.company?.default_hourly_rate || 35;
  const hoursPerSlot = formData.slot_duration === 'full_day' ? 7 : 3.5;

  // Calculate pricing
  const calculatePricing = () => {
    const numSlots = formData.selected_slots.length;
    if (numSlots === 0) return { perVisit: 0, total: 0, frequency: '', urssafTotal: 0 };

    const pricePerVisit = hoursPerSlot * hourlyRate;
    const totalPrice = pricePerVisit * numSlots;
    const urssafPrice = totalPrice * 0.5; // 50% credit d'impôt

    // Determine frequency text
    let frequencyText = '';
    if (numSlots === 1) {
      frequencyText = 'passage unique';
    } else {
      // Calculate average days between slots
      const sortedSlots = [...formData.selected_slots].sort((a, b) => new Date(a.date) - new Date(b.date));
      if (sortedSlots.length >= 2) {
        const firstDate = new Date(sortedSlots[0].date);
        const lastDate = new Date(sortedSlots[sortedSlots.length - 1].date);
        const daysDiff = Math.round((lastDate - firstDate) / (1000 * 60 * 60 * 24));
        const avgDaysBetween = daysDiff / (numSlots - 1);
        
        if (avgDaysBetween <= 7) {
          frequencyText = `${numSlots} passages / semaine`;
        } else if (avgDaysBetween <= 14) {
          frequencyText = `${numSlots} passages / 2 semaines`;
        } else if (avgDaysBetween <= 31) {
          frequencyText = `${numSlots} passages / mois`;
        } else if (avgDaysBetween <= 93) {
          frequencyText = `${numSlots} passages / 3 mois`;
        } else {
          frequencyText = `${numSlots} passages`;
        }
      }
    }

    return {
      perVisit: pricePerVisit,
      total: totalPrice,
      frequency: frequencyText,
      urssafTotal: urssafPrice,
      hoursTotal: hoursPerSlot * numSlots,
    };
  };

  const pricing = calculatePricing();

  // Check if a date has availability
  const getDateAvailability = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return availability.find(a => a.date === dateStr);
  };

  // Check if slot is already selected
  const isSlotSelected = (date, period) => {
    return formData.selected_slots.some(
      s => isSameDay(new Date(s.date), date) && s.period === period
    );
  };

  // Toggle slot selection
  const toggleSlot = (date, period) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const isSelected = isSlotSelected(date, period);

    if (isSelected) {
      setFormData({
        ...formData,
        selected_slots: formData.selected_slots.filter(
          s => !(s.date === dateStr && s.period === period)
        )
      });
    } else {
      setFormData({
        ...formData,
        selected_slots: [...formData.selected_slots, { date: dateStr, period }]
      });
    }
  };

  // Generate calendar days for current month
  const getCalendarDays = () => {
    const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  };

  // Toggle service selection
  const toggleService = (serviceId) => {
    if (formData.selected_services.includes(serviceId)) {
      setFormData({
        ...formData,
        selected_services: formData.selected_services.filter(s => s !== serviceId)
      });
    } else {
      setFormData({
        ...formData,
        selected_services: [...formData.selected_services, serviceId]
      });
    }
  };

  const validateStep = () => {
    switch (currentStep) {
      case 0:
        if (formData.selected_services.length === 0) {
          toast.error('Veuillez sélectionner au moins une prestation');
          return false;
        }
        if (formData.selected_services.includes('autre') && !formData.other_service_description.trim()) {
          toast.error('Veuillez préciser la prestation "Autre"');
          return false;
        }
        return true;
      case 1:
        if (formData.selected_slots.length === 0) {
          toast.error('Veuillez sélectionner au moins un créneau');
          return false;
        }
        return true;
      case 2:
        if (!formData.client_name || !formData.client_email || !formData.client_phone || !formData.client_address) {
          toast.error('Veuillez remplir tous les champs obligatoires');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep() && currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await widgetApi.submitBooking(companyId, {
        ...formData,
        pricing,
        hourly_rate: hourlyRate,
      });
      setSubmitted(true);
      toast.success('Demande envoyée avec succès!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Styles
  const containerStyle = { backgroundColor: 'transparent', color: textColor };
  const primaryBtnStyle = { backgroundColor: primaryColor, color: '#ffffff' };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" style={containerStyle}>
        <div className="animate-pulse flex flex-col items-center">
          <Loader2 className="w-12 h-12 animate-spin" style={{ color: primaryColor }} />
          <p className="mt-4" style={{ color: `${textColor}80` }}>Chargement...</p>
        </div>
      </div>
    );
  }

  if (!config || !config.company) {
    return (
      <div className="flex items-center justify-center p-4" style={containerStyle}>
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Leaf className="w-12 h-12 mx-auto mb-4" style={{ color: primaryColor }} />
            <p className="text-lg font-medium">Widget indisponible</p>
            <p className="mt-2 text-muted-foreground">Cette entreprise n'est pas active.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex items-center justify-center p-4" style={containerStyle}>
        <Card className="max-w-lg w-full shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-heading font-semibold">Demande envoyée!</h2>
            <p className="mt-2 text-muted-foreground">
              Votre demande a été transmise à {config.company.name}.
            </p>
            
            <div className="mt-6 p-4 bg-blue-50 rounded-xl text-left border border-blue-200">
              <h3 className="font-medium text-blue-800 mb-2">Prochaines étapes :</h3>
              <ol className="text-sm text-blue-700 space-y-2 list-decimal list-inside">
                <li>L'entreprise va valider vos créneaux</li>
                <li>Vous recevrez un devis définitif par email</li>
                <li>Une fois le devis accepté, vos RDV seront confirmés</li>
                <li>Vous recevrez une facture après chaque passage</li>
              </ol>
            </div>

            <div className="mt-6 p-4 rounded-xl text-left" style={{ backgroundColor: `${primaryColor}10` }}>
              <p className="text-sm text-muted-foreground">Estimation</p>
              <p className="text-2xl font-bold" style={{ color: primaryColor }}>
                {pricing.perVisit.toFixed(2)}€<span className="text-base font-normal text-muted-foreground"> / passage</span>
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {formData.selected_slots.length} passage(s) demandé(s) • {pricing.hoursTotal}h au total
              </p>
              {formData.urssaf_email && (
                <div className="mt-3 p-2 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-700 flex items-center gap-1">
                    <Check className="w-4 h-4" />
                    Avance Immédiate activée
                  </p>
                  <p className="text-lg font-semibold text-green-700">
                    {pricing.urssafTotal.toFixed(2)}€ après crédit d'impôt
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" style={containerStyle}>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div 
            className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-3"
            style={{ backgroundColor: primaryColor }}
          >
            <Leaf className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-heading font-semibold">{config.company.name}</h1>
          <p className="text-muted-foreground mt-1">Demande d'intervention régulière</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-6">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div 
                className="flex flex-col items-center"
              >
                <div
                  className="flex items-center justify-center w-10 h-10 rounded-full transition-colors"
                  style={index <= currentStep 
                    ? { backgroundColor: primaryColor, color: '#ffffff' }
                    : { backgroundColor: `${textColor}10`, color: `${textColor}50` }
                  }
                >
                  {index < currentStep ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                <span className="text-xs mt-1 hidden sm:block" style={{ color: index <= currentStep ? primaryColor : `${textColor}50` }}>
                  {step.title}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div 
                  className="w-8 sm:w-16 h-1 mx-1 sm:mx-2 rounded"
                  style={{ backgroundColor: index < currentStep ? primaryColor : `${textColor}15` }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <Card className="shadow-lg">
          {/* Step 1: Services Selection */}
          {currentStep === 0 && (
            <>
              <CardHeader>
                <CardTitle className="font-heading">Quelles prestations souhaitez-vous ?</CardTitle>
                <CardDescription>Sélectionnez une ou plusieurs prestations</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Services Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {DEFAULT_SERVICES.map((service) => (
                    <button
                      key={service.id}
                      onClick={() => toggleService(service.id)}
                      className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${
                        formData.selected_services.includes(service.id)
                          ? 'border-primary bg-primary/5 shadow-md'
                          : 'border-border hover:border-primary/50'
                      }`}
                      style={formData.selected_services.includes(service.id) ? { borderColor: primaryColor } : {}}
                    >
                      <span className="text-2xl mb-2">{service.icon}</span>
                      <span className="text-sm font-medium text-center">{service.label}</span>
                      {formData.selected_services.includes(service.id) && (
                        <Check className="w-4 h-4 mt-2" style={{ color: primaryColor }} />
                      )}
                    </button>
                  ))}
                </div>

                {/* Other service description */}
                {formData.selected_services.includes('autre') && (
                  <div className="space-y-2 p-4 bg-muted/50 rounded-xl">
                    <Label>Précisez votre prestation *</Label>
                    <Textarea
                      placeholder="Décrivez la prestation souhaitée..."
                      rows={2}
                      value={formData.other_service_description}
                      onChange={(e) => setFormData({ ...formData, other_service_description: e.target.value })}
                    />
                  </div>
                )}

                {/* Additional notes */}
                <div className="space-y-2">
                  <Label>Informations complémentaires (optionnel)</Label>
                  <Textarea
                    placeholder="Surface du terrain, particularités, accès..."
                    rows={3}
                    value={formData.needs_description}
                    onChange={(e) => setFormData({ ...formData, needs_description: e.target.value })}
                  />
                </div>

                {/* Duration Selection */}
                <div className="space-y-3">
                  <Label>Durée d'intervention souhaitée *</Label>
                  <RadioGroup
                    value={formData.slot_duration}
                    onValueChange={(value) => setFormData({ ...formData, slot_duration: value, selected_slots: [] })}
                    className="grid grid-cols-2 gap-4"
                  >
                    {DURATION_OPTIONS.map((option) => (
                      <Label
                        key={option.value}
                        className={`flex flex-col items-center p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                          formData.slot_duration === option.value 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border hover:border-primary/50'
                        }`}
                        style={formData.slot_duration === option.value ? { borderColor: primaryColor } : {}}
                      >
                        <RadioGroupItem value={option.value} className="sr-only" />
                        <Clock className="w-8 h-8 mb-2" style={{ color: formData.slot_duration === option.value ? primaryColor : '#888' }} />
                        <span className="font-medium">{option.label}</span>
                        <span className="text-sm text-muted-foreground">{option.hours}h</span>
                        <span className="text-lg font-bold mt-2" style={{ color: primaryColor }}>
                          {(option.hours * hourlyRate).toFixed(0)}€
                        </span>
                      </Label>
                    ))}
                  </RadioGroup>
                </div>

                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium">Tarif horaire : {hourlyRate}€/h</p>
                      <p className="mt-1">Le prix affiché correspond au montant par passage. Vous bénéficiez de 50% de crédit d'impôt si vous êtes éligible à l'Avance Immédiate (services à la personne).</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 2: Planning Selection */}
          {currentStep === 1 && (
            <>
              <CardHeader>
                <CardTitle className="font-heading">Choisissez vos créneaux</CardTitle>
                <CardDescription>Sélectionnez les dates souhaitées dans le calendrier</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Month Navigation */}
                <div className="flex items-center justify-between mb-4">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <h3 className="font-semibold capitalize">
                    {format(currentMonth, 'MMMM yyyy', { locale: fr })}
                  </h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>

                {/* Calendar Grid */}
                <div className="border rounded-xl overflow-hidden">
                  {/* Weekday Headers */}
                  <div className="grid grid-cols-7 bg-muted">
                    {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day) => (
                      <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  {/* Calendar Days */}
                  <div className="grid grid-cols-7">
                    {getCalendarDays().map((day, idx) => {
                      const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                      const isPast = isBefore(day, new Date()) && !isSameDay(day, new Date());
                      const dateAvail = getDateAvailability(day);
                      const hasMorning = dateAvail?.slots?.includes('morning');
                      const hasAfternoon = dateAvail?.slots?.includes('afternoon');
                      const morningSelected = isSlotSelected(day, 'morning');
                      const afternoonSelected = isSlotSelected(day, 'afternoon');
                      const fullDaySelected = isSlotSelected(day, 'full');

                      return (
                        <div 
                          key={idx}
                          className={`min-h-[80px] p-1 border-t border-r ${
                            !isCurrentMonth ? 'bg-muted/30' : ''
                          } ${isPast ? 'opacity-40' : ''}`}
                        >
                          <div className={`text-sm text-center mb-1 ${!isCurrentMonth ? 'text-muted-foreground' : ''}`}>
                            {format(day, 'd')}
                          </div>
                          
                          {isCurrentMonth && !isPast && (
                            <div className="space-y-1">
                              {formData.slot_duration === 'half_day' ? (
                                <>
                                  {/* Morning slot */}
                                  <button
                                    onClick={() => hasMorning && toggleSlot(day, 'morning')}
                                    disabled={!hasMorning}
                                    className={`w-full text-xs py-1 px-1 rounded transition-colors ${
                                      morningSelected 
                                        ? 'text-white' 
                                        : hasMorning 
                                          ? 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200' 
                                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    }`}
                                    style={morningSelected ? { backgroundColor: primaryColor } : {}}
                                  >
                                    Matin
                                  </button>
                                  {/* Afternoon slot */}
                                  <button
                                    onClick={() => hasAfternoon && toggleSlot(day, 'afternoon')}
                                    disabled={!hasAfternoon}
                                    className={`w-full text-xs py-1 px-1 rounded transition-colors ${
                                      afternoonSelected 
                                        ? 'text-white' 
                                        : hasAfternoon 
                                          ? 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200' 
                                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    }`}
                                    style={afternoonSelected ? { backgroundColor: primaryColor } : {}}
                                  >
                                    Après-midi
                                  </button>
                                </>
                              ) : (
                                /* Full day slot */
                                <button
                                  onClick={() => (hasMorning && hasAfternoon) && toggleSlot(day, 'full')}
                                  disabled={!(hasMorning && hasAfternoon)}
                                  className={`w-full text-xs py-2 px-1 rounded transition-colors ${
                                    fullDaySelected 
                                      ? 'text-white' 
                                      : (hasMorning && hasAfternoon)
                                        ? 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200' 
                                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  }`}
                                  style={fullDaySelected ? { backgroundColor: primaryColor } : {}}
                                >
                                  Journée
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Legend */}
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-green-50 border border-green-200"></div>
                    <span>Disponible</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: primaryColor }}></div>
                    <span>Sélectionné</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-gray-100"></div>
                    <span>Indisponible</span>
                  </div>
                </div>

                {/* Selected Slots Summary */}
                {formData.selected_slots.length > 0 && (
                  <div className="p-4 rounded-xl border" style={{ backgroundColor: `${primaryColor}05`, borderColor: `${primaryColor}30` }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Créneaux sélectionnés</span>
                      <Badge style={{ backgroundColor: primaryColor }}>{formData.selected_slots.length}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.selected_slots.map((slot, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center gap-1 px-2 py-1 bg-white rounded-lg border text-sm"
                        >
                          <CalendarDays className="w-3 h-3" />
                          {format(new Date(slot.date), 'd MMM', { locale: fr })}
                          <span className="text-muted-foreground">
                            ({slot.period === 'morning' ? 'Matin' : slot.period === 'afternoon' ? 'Après-midi' : 'Journée'})
                          </span>
                          <button 
                            onClick={() => toggleSlot(new Date(slot.date), slot.period)}
                            className="ml-1 text-red-500 hover:text-red-700"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t flex items-center justify-between">
                      <span className="text-muted-foreground">Total estimation</span>
                      <span className="text-xl font-bold" style={{ color: primaryColor }}>
                        {pricing.total.toFixed(2)}€
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </>
          )}

          {/* Step 3: Contact Information */}
          {currentStep === 2 && (
            <>
              <CardHeader>
                <CardTitle className="font-heading">Vos coordonnées</CardTitle>
                <CardDescription>Informations nécessaires pour vous contacter</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nom complet *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Jean Dupont"
                        className="pl-10"
                        value={formData.client_name}
                        onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Téléphone *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="tel"
                        placeholder="06 12 34 56 78"
                        className="pl-10"
                        value={formData.client_phone}
                        onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="jean.dupont@email.com"
                      className="pl-10"
                      value={formData.client_email}
                      onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Adresse d'intervention *</Label>
                  <AddressAutocomplete
                    value={formData.client_address}
                    onChange={(v) => setFormData({ ...formData, client_address: v })}
                    onSelect={(sel) => setFormData(prev => ({
                      ...prev,
                      client_address: sel.label,
                      client_coords: sel.coords || null,
                    }))}
                    placeholder="Commencez à taper votre adresse…"
                    data-testid="booking-address-autocomplete"
                    savedAddresses={savedAddresses}
                    required
                  />
                </div>

                {/* URSSAF Email Section */}
                <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                  <div className="flex items-start gap-3">
                    <Euro className="w-5 h-5 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-medium text-green-800">Avance Immédiate de crédit d'impôt</h4>
                      <p className="text-sm text-green-700 mt-1">
                        Bénéficiez de 50% de réduction immédiate sur vos paiements grâce au crédit d'impôt pour l'emploi à domicile.
                      </p>
                      <a 
                        href="https://www.urssaf.fr/portail/home/particulier-employeur/particulier-employeur/avance-immediate.html" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-green-700 hover:text-green-900 mt-2 underline"
                      >
                        <ExternalLink className="w-3 h-3" />
                        En savoir plus sur l'Avance Immédiate
                      </a>
                      
                      <div className="mt-3 space-y-2">
                        <Label className="text-green-800">Email lié aux services des impôts (optionnel)</Label>
                        <Input
                          type="email"
                          placeholder="votre-email@impots.gouv.fr"
                          value={formData.urssaf_email}
                          onChange={(e) => setFormData({ ...formData, urssaf_email: e.target.value })}
                          className="bg-white"
                        />
                        <p className="text-xs text-green-600">
                          Renseignez l'email utilisé sur impots.gouv.fr pour activer l'Avance Immédiate
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes complémentaires</Label>
                  <Textarea
                    placeholder="Informations supplémentaires, accès au jardin, code portail..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
              </CardContent>
            </>
          )}

          {/* Step 4: Summary */}
          {currentStep === 3 && (
            <>
              <CardHeader>
                <CardTitle className="font-heading">Récapitulatif de votre demande</CardTitle>
                <CardDescription>Vérifiez les informations avant envoi</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Services Summary */}
                <div className="p-4 bg-muted/50 rounded-xl">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Prestations demandées
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {formData.selected_services.map(serviceId => {
                      const service = DEFAULT_SERVICES.find(s => s.id === serviceId);
                      return (
                        <Badge key={serviceId} variant="secondary" className="text-sm">
                          {service?.icon} {service?.label}
                        </Badge>
                      );
                    })}
                  </div>
                  {formData.other_service_description && (
                    <p className="text-sm text-muted-foreground mt-2">
                      Autre : {formData.other_service_description}
                    </p>
                  )}
                  {formData.needs_description && (
                    <p className="text-sm text-muted-foreground mt-2">
                      {formData.needs_description}
                    </p>
                  )}
                </div>

                {/* Slots Summary */}
                <div className="p-4 bg-muted/50 rounded-xl">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Créneaux demandés ({formData.selected_slots.length})
                  </h4>
                  <div className="space-y-1">
                    {formData.selected_slots.map((slot, idx) => (
                      <p key={idx} className="text-sm text-muted-foreground">
                        • {format(new Date(slot.date), 'EEEE d MMMM yyyy', { locale: fr })} - {
                          slot.period === 'morning' ? '½ journée matin (8h-11h30)' : 
                          slot.period === 'afternoon' ? '½ journée après-midi (14h-17h30)' : 
                          'Journée complète (8h-15h)'
                        }
                      </p>
                    ))}
                  </div>
                </div>

                {/* Contact Summary */}
                <div className="p-4 bg-muted/50 rounded-xl">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Coordonnées
                  </h4>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>{formData.client_name}</p>
                    <p>{formData.client_email}</p>
                    <p>{formData.client_phone}</p>
                    <p>{formData.client_address}</p>
                    {formData.urssaf_email && (
                      <p className="text-green-600">✓ Avance Immédiate : {formData.urssaf_email}</p>
                    )}
                  </div>
                </div>

                {/* Pricing Summary */}
                <div className="p-4 rounded-xl border-2" style={{ borderColor: primaryColor, backgroundColor: `${primaryColor}05` }}>
                  <h4 className="font-medium mb-3">Estimation tarifaire</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Type d'intervention</span>
                      <span>{formData.slot_duration === 'full_day' ? 'Journée complète (7h)' : 'Demi-journée (3h30)'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Tarif horaire</span>
                      <span>{hourlyRate}€/h</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Prix par passage</span>
                      <span className="font-medium">{pricing.perVisit.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Nombre de passages</span>
                      <span>{formData.selected_slots.length}</span>
                    </div>
                    <hr className="my-2" />
                    <div className="flex justify-between font-medium">
                      <span>Total estimé</span>
                      <span className="text-lg" style={{ color: primaryColor }}>{pricing.total.toFixed(2)}€</span>
                    </div>
                    {formData.urssaf_email && (
                      <div className="flex justify-between text-green-600 font-medium">
                        <span>Après crédit d'impôt (50%)</span>
                        <span className="text-lg">{pricing.urssafTotal.toFixed(2)}€</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium">Information importante</p>
                      <p className="mt-1">
                        Cette demande n'est pas un engagement. L'entreprise vous contactera pour valider 
                        les créneaux et vous enverra un devis définitif. Vous recevrez une facture après 
                        chaque passage effectué.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between p-6 pt-0">
            {currentStep > 0 ? (
              <Button variant="outline" onClick={prevStep} className="rounded-full">
                <ChevronLeft className="w-4 h-4 mr-2" />
                Précédent
              </Button>
            ) : (
              <div />
            )}
            
            {currentStep < STEPS.length - 1 ? (
              <Button onClick={nextStep} className="rounded-full" style={primaryBtnStyle}>
                Suivant
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                disabled={isSubmitting}
                className="rounded-full"
                style={primaryBtnStyle}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer la demande
                  </>
                )}
              </Button>
            )}
          </div>
        </Card>

        {/* Footer - Propulsé par ResaWork + Widget ID (hidden for Premium subscribers) */}
        <WidgetFooter widgetId={companyId} isPremium={config?.is_premium} />
      </div>
    </div>
  );
}
